import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Breadcrumbs, PageHeader, Tabs, type TabDef } from "@/components/ui";
import { AssemblyPanel } from "@/components/catalog-tabs/assembly-panel";
import { ListsPanel } from "@/components/catalog-tabs/lists-panel";
import { ManufacturersPanel } from "@/components/catalog-tabs/manufacturers-panel";

type CatalogTab = "assemblies" | "lists" | "manufacturers";

const ALLOWED_TABS: CatalogTab[] = ["assemblies", "lists", "manufacturers"];

interface CatalogAssembliesSearch {
  tab?: CatalogTab;
}

function parseTab(value: unknown): CatalogTab | undefined {
  if (typeof value !== "string") return undefined;
  return ALLOWED_TABS.find((tab) => tab === value);
}

export const Route = createFileRoute("/_authenticated/catalog/assemblies/")({
  component: CatalogAssembliesPage,
  validateSearch: (search: Record<string, unknown>): CatalogAssembliesSearch => ({
    tab: parseTab(search.tab),
  }),
});

/**
 * The consolidated assembly catalog page: one tabbed surface over the three
 * former catalog pages (assemblies list, catalog lists management,
 * manufacturers list). Only the active tab's panel mounts; the tab rides
 * the `?tab=` search param against the allow-list above (absent or unknown
 * → assemblies) and tab switches navigate with `replace: true` so history
 * keeps one entry per page, not per tab.
 */
function CatalogAssembliesPage() {
  const { t } = useTranslation(["catalog", "nav"]);
  const navigate = useNavigate();
  const { tab } = Route.useSearch();

  const activeTab: CatalogTab = tab ?? "assemblies";

  const setActiveTab = (next: CatalogTab) => {
    void navigate({
      to: "/catalog/assemblies",
      search: next === "assemblies" ? {} : { tab: next },
      replace: true,
    });
  };

  const tabs: TabDef<CatalogTab>[] = [
    { value: "assemblies", label: t("page.tabs.assemblies") },
    { value: "lists", label: t("page.tabs.lists") },
    { value: "manufacturers", label: t("page.tabs.manufacturers") },
  ];

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[{ label: t("catalog.group", { ns: "nav" }) }, { label: t("page.assembliesTitle") }]}
      />
      <PageHeader title={t("page.assembliesTitle")} />

      <div className="mt-6">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          ariaLabel={t("page.assembliesTitle")}
        />

        <div
          className="mt-6"
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === "assemblies" && <AssemblyPanel />}
          {activeTab === "lists" && <ListsPanel />}
          {activeTab === "manufacturers" && <ManufacturersPanel />}
        </div>
      </div>
    </div>
  );
}
